/**
 * @author: lv.chen
 * @date: 2025/8/7
 * @desc: EditReport组件，编辑报告组件
 */
import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { history, useIntl } from 'umi';
import { cloneDeep } from 'lodash-es';
import { Layout } from 'tea-component';
import Loading from '@home/components/Loading';
import ReportForm, { IReportFormInstance } from './components/ReportForm';
import usePageLog, { reportButtonLog } from '@user-center/_hooks/usePageLog';
import useSearchParams from '@gp/hooks/useSearchParams';
import useReportAdminMode from './_hooks/useReportAdminMode';
import useReportOptions from './_hooks/useReportOptions';
import useReportPublish from './_hooks/useReportPublish';
import { fetchReports, updateReport, handleAddUpdateReportError } from './services';
import { ReportState } from '@user-center/enums/reports';
import { DEFAULT_REPORT } from './constants';
import { getReportMutationParams } from './utils/reportParams';

const { Body, Content } = Layout;

interface IEditReportProps extends IBaseProps {}

const EditReport: React.FC<IEditReportProps> = (props) => {
  const { style, className } = props;

  const { locale, formatMessage } = useIntl();

  const { resourceId } = useSearchParams();

  const { loading: adminModeLoading, adminMode } = useReportAdminMode();

  const { permissionOptionsLoading, permissionOptions } = useReportOptions(locale);

  const reportFormInstance = useRef<IReportFormInstance | null>(null);

  const report = useRef<IReport | null>(null);

  const [loading, setLoading] = useState(true);

  const { reportPublishLoading, onReportPublish } = useReportPublish(adminMode, report);

  const initializeReport = async () => {
    if (!resourceId) return;

    try {
      setLoading(true);
      const res = await fetchReports({ admin_mode: adminMode, resource_ids: [resourceId] });
      const reportTmp = res?.data?.list?.[0];
      if (!!reportTmp) {
        report.current = cloneDeep(reportTmp);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading || permissionOptionsLoading || adminModeLoading) {
      return;
    }
    reportFormInstance.current?.initialize(report.current!);
  }, [loading, permissionOptionsLoading, adminModeLoading]);

  useEffect(() => {
    if (adminModeLoading) {
      return;
    }
    initializeReport();
  }, [adminModeLoading, resourceId, adminMode]);

  usePageLog('contentManagementCenterReportsEdit');

  return (
    <Layout style={style} className={classnames('dbu-edit-report-layout', className)}>
      {(loading || adminModeLoading) && (
        <div className="dbu-edit-report-layout__loading-wrapper">
          <Loading />
        </div>
      )}
      <Body>
        <Content>
          <Content.Header
            showBackButton
            title={formatMessage({ id: 'dbu_common_report' })}
            onBackButtonClick={(e) => {
              e?.stopPropagation();
              history.goBack();
            }}
          />
          <Content.Body>
            <ReportForm
              className="dbu-add-report-form"
              ref={reportFormInstance}
              reportPublishLoading={reportPublishLoading}
              permissionOptions={permissionOptions}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onConfirm={async (reportPayload: IReportPartial) => {
                try {
                  const reportNew = {
                    ...DEFAULT_REPORT,
                    ...report.current,
                    ...reportPayload,
                    state: report.current?.state ?? ReportState.UnPublished,
                  };
                  await updateReport(reportNew, getReportMutationParams(adminMode, reportNew));
                  report.current = cloneDeep(reportNew);
                  reportFormInstance.current?.preview(cloneDeep(reportNew));
                } catch (error) {
                  handleAddUpdateReportError(formatMessage, error);
                  console.error(error);
                }
                reportButtonLog('contentManagementCenterReportsEditSaveSubmit');
              }}
              onBack={() => {
                reportButtonLog('contentManagementCenterReportsEditBackSubmit');
              }}
              onPreview={() => {
                reportButtonLog('contentManagementCenterReportsEditPreviewSubmit');
              }}
              onPublish={(payload: IReportPartial) => {
                onReportPublish(payload);
                reportButtonLog('contentManagementCenterReportsEditShareSubmit');
              }}
              onUploadCover={() => {
                reportButtonLog('contentManagementCenterReportsEditUploadCoverSubmit');
              }}
              onUploadCreatorAvatar={() => {
                reportButtonLog('contentManagementCenterReportsEditUploadAvatarSubmit');
              }}
            />
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
};

export default EditReport;
